import React, { useEffect, useRef } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CATEGORIES } from '../../helpers/parkingEvents';
import { catColor, dateClock } from './eventItems';

const MAX_ROWS = 1000;

function rowColor(theme, item, selected) {
    if (item.k === selected) return theme.palette.action.selected;
    if (item.sev === 'fail') return alpha(theme.palette.error.main, 0.08);
    if (item.sev === 'warn') return alpha(theme.palette.warning.main, 0.08);
    return 'transparent';
}

/**
 * Vertical list of timeline items (newest last), failures tinted, the selected one scrolled into view
 */
export default function EventList({
    items,
    selected,
    onSelect,
    showPlate = false,
    maxHeight = 520,
}) {
    const refs = useRef({});

    useEffect(() => {
        const el = selected && refs.current[selected];
        if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [selected]);

    if (!items.length) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                Nincs a szűrésnek megfelelő esemény.
            </Typography>
        );
    }

    return (
        <Box sx={{ maxHeight, overflowY: 'auto' }}>
            {items.slice(0, MAX_ROWS).map((i) => (
                <Stack
                    key={i.k}
                    ref={(el) => {
                        refs.current[i.k] = el;
                    }}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    onClick={() => onSelect && onSelect(i.k)}
                    sx={(theme) => ({
                        px: 1.5,
                        py: 0.75,
                        cursor: onSelect ? 'pointer' : 'default',
                        borderLeft: `4px solid ${catColor(i.cat)}`,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        opacity: i.context ? 0.7 : 1,
                        bgcolor: rowColor(theme, i, selected),
                        '&:hover': { bgcolor: theme.palette.action.hover },
                    })}
                >
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap', minWidth: 132 }}
                    >
                        {dateClock(i.ts)}
                    </Typography>
                    <Chip
                        size="small"
                        label={(CATEGORIES[i.cat] || CATEGORIES.other).label}
                        sx={{ bgcolor: catColor(i.cat), color: '#fff', minWidth: 96 }}
                    />
                    {showPlate && (
                        <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontWeight: 700, minWidth: 90 }}
                        >
                            {i.plate || '–'}
                        </Typography>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: i.sev === 'info' ? 400 : 700 }}
                            color={i.sev === 'fail' ? 'error.main' : 'text.primary'}
                        >
                            {i.label}
                            {i.amount
                                ? ` · ${new Intl.NumberFormat('hu-HU').format(i.amount)} Ft`
                                : ''}
                        </Typography>
                        {(i.detail || i.user || i.context) && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                component="div"
                            >
                                {[i.context && 'kapu esemény', i.detail, i.user]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </Typography>
                        )}
                    </Box>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {i.gateLabel}
                    </Typography>
                </Stack>
            ))}
            {items.length > MAX_ROWS && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {MAX_ROWS} látható a {items.length} eseményből, szűkíts a szűrőkkel.
                </Typography>
            )}
        </Box>
    );
}
